import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Attach Bearer token to every request from localStorage
axios.interceptors.request.use(config => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// Auto-refresh interceptor: catches 401, refreshes token, retries request
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve();
    });
    failedQueue = [];
};

axios.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;
        // Only intercept auth-worthy 401s. Refresh/login endpoints handle
        // their own failure — never loop them through here.
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/login')) {
            const refreshToken = localStorage.getItem('refresh_token');
            // No refresh token → soft signout. Fire a custom event so the
            // AuthProvider flips `user` to false; React Router handles the
            // redirect naturally instead of a hard window.location.assign,
            // which was blowing away the session on every browser-back that
            // happened to fire a stale API call.
            if (!refreshToken) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                try { window.dispatchEvent(new CustomEvent('betohalics:auth-expired')); } catch { /* ignore */ }
                const sessionErr = new Error('Your session expired. Please sign in again.');
                sessionErr.response = { status: 401, data: { detail: 'Your session expired. Please sign in again.' } };
                return Promise.reject(sessionErr);
            }
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => axios(originalRequest));
            }
            originalRequest._retry = true;
            isRefreshing = true;
            try {
                const resp = await axios.post(`${API}/auth/refresh`, {}, {
                    withCredentials: true,
                    headers: { 'Authorization': `Bearer ${refreshToken}` },
                });
                if (resp.data?.access_token) {
                    localStorage.setItem('access_token', resp.data.access_token);
                }
                if (resp.data?.refresh_token) {
                    // Some servers rotate the refresh token on every use — keep
                    // the fresh one so the next 401 doesn't loop-fail with a
                    // stale one we already spent.
                    localStorage.setItem('refresh_token', resp.data.refresh_token);
                }
                processQueue(null);
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError);
                // ONLY sign out on a hard refresh failure (4xx from the refresh
                // endpoint itself). Network blips (no response, CORS glitch,
                // dropped signal) MUST NOT sign the user out — they were the
                // #1 cause of the "back button kicked me to login" reports.
                const refreshHard4xx =
                    refreshError?.response?.status &&
                    refreshError.response.status >= 400 &&
                    refreshError.response.status < 500;
                if (refreshHard4xx) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    try { window.dispatchEvent(new CustomEvent('betohalics:auth-expired')); } catch { /* ignore */ }
                }
                const sessionErr = new Error(refreshHard4xx
                    ? 'Your session expired. Please sign in again.'
                    : 'Network hiccup — please retry.');
                sessionErr.response = { status: 401, data: { detail: sessionErr.message } };
                return Promise.reject(sessionErr);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
        } catch (err) {
            // CRITICAL: only sign out on a HARD 4xx from /auth/me. Network
            // errors (no response object) and 5xx blips MUST keep the
            // session — otherwise a flaky cell tower on a back-nav wipes
            // the user's login. (Root cause of the "back button signs me
            // out" bug — Feb 2026.) The refresh interceptor will still get
            // a chance to rotate the token on a real 401.
            const hard4xx = err?.response?.status && err.response.status >= 400 && err.response.status < 500;
            if (hard4xx) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                setUser(false);
            }
            // else: preserve whatever state we already had (null or previous
            // user object). Next successful call will settle it.
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Global "session expired" event — the axios interceptor dispatches
    // this on a HARD refresh-token failure. We flip `user` to false and
    // let React Router redirect naturally. No more `window.location.assign`
    // hard-reloading and wiping the SPA state on every stray 401.
    useEffect(() => {
        const onExpired = () => setUser(false);
        window.addEventListener('betohalics:auth-expired', onExpired);
        return () => window.removeEventListener('betohalics:auth-expired', onExpired);
    }, []);

    // iOS Safari back-forward cache (bfcache) restores the page in a
    // frozen state — any in-flight XHR gets rejected on restore, which
    // used to trigger the sign-out. Silent re-check on pageshow keeps
    // the session synced without wiping it.
    useEffect(() => {
        const onShow = (e) => {
            if (e.persisted) {
                // Restored from bfcache — refresh in the background, keep
                // whatever `user` we already had if the refresh fails.
                checkAuth();
            }
        };
        window.addEventListener('pageshow', onShow);
        return () => window.removeEventListener('pageshow', onShow);
    }, [checkAuth]);

    // Keep `user.credits` (and rollover, etc.) globally fresh:
    // poll /auth/me every 20s and refetch when the tab regains focus.
    // Without this, Header showed a stale balance while Lobby's own poll
    // was fresh, leading to two different numbers on screen at the same time.
    useEffect(() => {
        if (!user || user === false) return; // not logged in
        let cancelled = false;
        const refresh = async () => {
            try {
                const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
                if (!cancelled) setUser(data);
            } catch { /* network blip — keep current state */ }
        };
        const id = setInterval(refresh, 20_000);
        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async (email, password) => {
        // Helper that wraps the network call with a timeout + single auto-retry
        // so users on flaky cellular (low signal, iOS Low Power Mode, brief
        // carrier hiccups) don't get a scary "can't reach the server" error
        // on the first dropped packet.
        const attemptLogin = async (attempt) => {
            return await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true, timeout: 15000 },
            );
        };
        try {
            let data;
            try {
                ({ data } = await attemptLogin(1));
            } catch (firstErr) {
                // Only auto-retry on actual network errors / timeouts — NOT on
                // auth rejections (400/401/etc.). One retry, 600ms backoff.
                const isNetworkLike =
                    firstErr.code === 'ECONNABORTED' ||
                    firstErr.message === 'Network Error' ||
                    !firstErr.response;
                if (!isNetworkLike) throw firstErr;
                await new Promise(r => setTimeout(r, 600));
                ({ data } = await attemptLogin(2));
            }
            // Store tokens in localStorage
            if (data.access_token) localStorage.setItem('access_token', data.access_token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
            setUser(data);
            return { success: true, data };
        } catch (e) {
            // Build a much more useful error string. The generic "Something
            // went wrong" hid the real cause (server down, CORS, wrong env)
            // and made production triage painful.
            let error;
            if (e.response?.data?.detail) {
                error = formatApiErrorDetail(e.response.data.detail);
            } else if (e.response?.status >= 500) {
                error = `Server error (${e.response.status}). Try again in a moment — if this keeps happening, contact support.`;
            } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                error = `You appear to be offline. Reconnect to Wi-Fi or cellular and try again.`;
            } else if (e.code === 'ECONNABORTED') {
                error = `The request timed out. Your connection looks slow — try Wi-Fi, turn off Low Power Mode, or try again.`;
            } else if (e.message === 'Network Error' || !e.response) {
                error = `Can't reach the server. If your signal is weak or your battery is in Low Power Mode, switch to Wi-Fi and try again.`;
            } else {
                error = e.message || 'Login failed.';
            }
            // Log full context so we can see it in browser/Safari console too.
            // eslint-disable-next-line no-console
            console.error('[login]', { status: e.response?.status, data: e.response?.data, message: e.message, code: e.code });
            return { success: false, error };
        }
    };

    const register = async ({ name, phone, password, email = null, referral_code = null }) => {
        try {
            const payload = { name, phone, password };
            if (email) payload.email = email;
            if (referral_code) payload.referral_code = referral_code;
            // Capture device fingerprint for anti-fraud — backend uses this to
            // detect dupes from the same browser/phone (catches Gestine making
            // a second account with a different phone number).
            let fingerprint = '';
            try {
                const { getDeviceFingerprint } = await import('../utils/deviceFingerprint');
                fingerprint = getDeviceFingerprint();
            } catch { /* never block registration on fingerprint failure */ }

            const { data } = await axios.post(`${API}/auth/register`, payload, {
                withCredentials: true,
                headers: fingerprint ? { 'X-Device-Fingerprint': fingerprint } : {},
            });

            // Self-signups land in pending_approval; backend deliberately does
            // NOT issue tokens. The Register page will show a "waiting for
            // admin approval" screen instead of logging the player in.
            if (data.pending_approval) {
                return { success: true, data, pending_approval: true };
            }
            // Legacy path (in case the gate is turned off some day)
            if (data.access_token) localStorage.setItem('access_token', data.access_token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
            setUser(data);
            return { success: true, data };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } finally {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(false);
        }
    };

    const refreshUser = async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
        } catch {
            // Silently fail
        }
    };

    const updateCredits = (newCredits) => {
        if (user) {
            setUser({ ...user, credits: newCredits });
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, updateCredits }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
